import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import clsx from 'clsx';
import styled from 'styled-components';

import { formatUserDateFromString } from '@arrived/common';

import { PortfolioInfoIcon } from '../PortfolioInfoIcon';
import { PortfolioTooltip } from '../PortfolioTooltip';
import { PrivacyContextAwareMoneyValue } from '../PrivacyContextAwareMoneyValue';
import { RentalIncomePercentText } from '../RentalIncomePercentText';
import { RentalIncomePercentTypography } from '../RentalIncomePercentTypography';
import { DetailsListFinalItemTypography } from './DetailsListFinalItemTypography';
import { DetailsListItem } from './DetailsListItem';
import { DetailsListLineItem } from './DetailsListLineItem';
import { DetailsListLineItemTypography } from './DetailsListLineItemTypography';

import { RENTAL_INCOME_COLOR } from '../portfolio.constants';

const StyledAccordionDetailsListItem = styled(DetailsListItem)`
  &.all-time-rental-income {
    align-items: flex-start;
  }

  .value {
    align-items: flex-end;

    & > * {
      text-align: right;
    }
  }

  .text-column {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(0.5)};
  }

  .rental-income-percent {
    white-space: nowrap;
  }
`;

export interface RentalIncomeDetailsListProps {
  allTimeDividends: number;
  prevDividend?: number;
  prevDividendDate?: string;
  rentalIncomePercent: number;
}

export const RentalIncomeDetailsList = ({
  allTimeDividends,
  prevDividend,
  prevDividendDate,
  rentalIncomePercent,
}: RentalIncomeDetailsListProps) => {
  const dividendDate = useMemo(
    () => (prevDividendDate ? formatUserDateFromString(prevDividendDate, 'QQQ, yyyy') : undefined),
    [prevDividendDate]
  );

  return (
    <>
      <DetailsListLineItem value={prevDividend ?? 0}>
        <FormattedMessage
          id="last-dividend"
          defaultMessage="Last Dividend (Q{lastDividendQ})"
          values={{ lastDividendQ: dividendDate }}
        />
      </DetailsListLineItem>
      <DetailsListLineItem value={allTimeDividends - (prevDividend ?? 0)}>
        <FormattedMessage id="all-prev-dividends" defaultMessage="All Prev Dividends" />
      </DetailsListLineItem>
      <StyledAccordionDetailsListItem className="all-time-rental-income">
        <div className="text-column">
          <PortfolioTooltip
            title={
              <FormattedMessage
                id="rental-income-description"
                defaultMessage="We pay dividends quarterly based on the % of your shares owned and rental income generated from your properties each month. These are realized gains and are deposited directly into your bank account, therefore it isn't included in your portfolio balance."
              />
            }
          >
            <DetailsListFinalItemTypography>
              <FormattedMessage id="rental-income-all-time" defaultMessage="Rental Income (all time)" />
              <PortfolioInfoIcon />
            </DetailsListFinalItemTypography>
          </PortfolioTooltip>
          <DetailsListLineItemTypography variant="body2">
            <FormattedMessage
              id="rental-income-description"
              defaultMessage="Withdrawn from Arrived and sent to your linked bank account"
            />
          </DetailsListLineItemTypography>
        </div>
        <div className={clsx('value', 'text-column')}>
          <DetailsListFinalItemTypography color={RENTAL_INCOME_COLOR}>
            <PrivacyContextAwareMoneyValue value={allTimeDividends} length={4} />
          </DetailsListFinalItemTypography>
          <RentalIncomePercentTypography className="rental-income-percent">
            (<RentalIncomePercentText rentalIncomePercent={rentalIncomePercent} />)
          </RentalIncomePercentTypography>
        </div>
      </StyledAccordionDetailsListItem>
    </>
  );
};
